import React, { useEffect, useState } from 'react';
import { Link as LinkIcon, Trash2, Plus, Minus } from 'lucide-react';
import { apiClient } from '../packages/api-client/index';

interface RecordRelation {
  id: string;
  from_entity_id: string;
  to_entity_id: string;
  relation_type: string;
  to_entity_name: string;
  to_entity_type: string;
  created_at: string;
}

const RELATION_TYPES = [
  { value: 'related_to',   label: 'Related to' },
  { value: 'parent_of',    label: 'Parent of' },
  { value: 'subsidiary_of',label: 'Subsidiary of' },
  { value: 'blocks',       label: 'Blocks' },
  { value: 'duplicates',   label: 'Duplicates' },
];

interface Props {
  entityId: string;
}

export function RelatedRecords({ entityId }: Props) {
  const [relations, setRelations] = useState<RecordRelation[]>([]);
  const [loading, setLoading] = useState(true);
  const [toEntityId, setToEntityId] = useState('');
  const [relationType, setRelationType] = useState('related_to');
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    apiClient.get<{ data: RecordRelation[] }>(`/records/${entityId}/relations`)
      .then(r => setRelations(r.data.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [entityId]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!toEntityId.trim()) return;
    setAdding(true);
    try {
      const { data } = await apiClient.post<RecordRelation>(`/records/${entityId}/relations`, {
        to_entity_id: toEntityId.trim(),
        relation_type: relationType,
      });
      setRelations(prev => [data, ...prev]);
      setToEntityId('');
    } finally { setAdding(false); }
  }

  async function handleDelete(id: string) {
    await apiClient.delete(`/record-relations/${id}`);
    setRelations(prev => prev.filter(r => r.id !== id));
  }

  return (
    <div className="related-records-card card">
      <div className="related-records-header">
        <span style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}><LinkIcon size={16} aria-hidden="true" /> Related Records</span>
        <button className="btn btn-ghost btn-sm" onClick={() => setOpen(o => !o)}>
          {open
            ? <><Minus size={14} aria-hidden="true" style={{ verticalAlign: 'text-bottom' }} /> Hide form</>
            : <><Plus size={14} aria-hidden="true" style={{ verticalAlign: 'text-bottom' }} /> Add Relation</>}
        </button>
      </div>

      {open && (
        <form onSubmit={handleAdd} className="related-records-form">
          <input
            className="form-input"
            placeholder="Target record ID…"
            value={toEntityId}
            onChange={e => setToEntityId(e.target.value)}
            style={{ flex: 1 }}
          />
          <select className="form-input" value={relationType} onChange={e => setRelationType(e.target.value)}
            style={{ width: 'auto' }}>
            {RELATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" type="submit" disabled={adding}>
            {adding ? '…' : 'Link'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="spinner" style={{ margin: '1rem auto' }} />
      ) : relations.length === 0 ? (
        <p className="related-records-empty">No related records yet</p>
      ) : (
        <ul className="related-records-list">
          {relations.map(rel => {
            const isSrc = rel.from_entity_id === entityId;
            const otherName = isSrc ? (rel.to_entity_name || rel.to_entity_id) : (rel.from_entity_id);
            const typeLbl = RELATION_TYPES.find(t => t.value === rel.relation_type)?.label ?? rel.relation_type;
            return (
              <li key={rel.id} className="related-record-row">
                <span className="related-record-type">{isSrc ? typeLbl : '← ' + typeLbl}</span>
                <span className="related-record-name">{otherName}</span>
                {rel.to_entity_type && <span className="related-record-meta">{rel.to_entity_type}</span>}
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)', padding: '2px 5px', marginLeft: 'auto' }}
                  aria-label="Delete relation" onClick={() => handleDelete(rel.id)}><Trash2 size={14} aria-hidden="true" /></button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
